import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Select } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import Button from 'components/generalUI/Button';
import FeatureIcon from 'components/generalUI/FeatureIcon';
import priceFormat from 'utils/formats/priceFormat';
import { AdditionalServicesState } from 'views/TicketSummary/ticketSummary.viewModel';
import styles from './AdditionalServicesCard.module.scss';

type AdditionalServicesCardProps = AdditionalServicesState;

const AdditionalServicesCard: FC<AdditionalServicesCardProps> = (
  props: AdditionalServicesCardProps
) => {
  const { t } = useTranslation();
  const {
    grills,
    price,
    selectedId,
    isApplied,
    unavailableReason,
    loading,
    onSelect,
    onToggle,
    canopy,
  } = props;
  const [showRequirements, setShowRequirements] = useState(false);

  const canopyRequirements = t(
    'pages.ticketSummary.additionalServices.canopyRequirements',
    {
      returnObjects: true,
    }
  ) as unknown as string[];

  const canopyUnavailableMessage =
    canopy.unavailableReason === 'soldOut'
      ? t('pages.ticketSummary.additionalServices.canopyUnavailableSoldOut')
      : t('pages.ticketSummary.additionalServices.canopyUnavailableDisabled');

  const renderCanopyAction = () => {
    if (canopy.unavailableReason) {
      return <p className={styles.Unavailable}>{canopyUnavailableMessage}</p>;
    }
    if (!canopy.isApplied) {
      return (
        <Button
          kind="secondary"
          className={styles.SubmitButton}
          disabled={canopy.loading || !canopy.max}
          onClick={canopy.onAdd}
        >
          {t('pages.ticketSummary.additionalServices.add')}
        </Button>
      );
    }
    return (
      <div className={styles.Counter}>
        <Button
          kind="secondary"
          shape="circle"
          className={styles.CounterButton}
          onClick={() => canopy.onChangeCount(canopy.count - 1)}
          aria-label={t('pages.ticketSummary.additionalServices.removeCanopyTicket')}
        >
          <MinusOutlined />
        </Button>
        <span className={styles.Count}>{canopy.count}</span>
        <Button
          kind="secondary"
          shape="circle"
          className={styles.CounterButton}
          disabled={canopy.count >= canopy.max}
          onClick={() => canopy.onChangeCount(canopy.count + 1)}
          aria-label={t('pages.ticketSummary.additionalServices.addCanopyTicket')}
        >
          <PlusOutlined />
        </Button>
      </div>
    );
  };

  const options = grills.map((grill) => ({
    value: grill.id,
    disabled: grill.disabled,
    label: grill.disabled
      ? `${grill.name} ${t('pages.ticketSummary.additionalServices.reserved')}`
      : grill.name,
  }));

  const unavailableMessage =
    unavailableReason === 'reserved'
      ? t('pages.ticketSummary.additionalServices.unavailableReserved')
      : t('pages.ticketSummary.additionalServices.unavailableDisabled');

  return (
    <div className={styles.Card}>
      <h3 className={styles.Title}>
        {t('pages.ticketSummary.additionalServices.title')}
      </h3>

      <div className={styles.Service}>
        <FeatureIcon
          icon="grill"
          description={t('pages.ticketSummary.additionalServices.grillQuestion')}
        />
        <div className={styles.ServiceText}>
          <div className={styles.ServiceHeader}>
            <p className={styles.Question}>
              {t('pages.ticketSummary.additionalServices.grillQuestion')}
            </p>
            {price > 0 && (
              <span className={styles.Price}>{priceFormat.format(price)}</span>
            )}
          </div>
          <p className={styles.Description}>
            {t('pages.ticketSummary.additionalServices.grillDescription')}
          </p>
        </div>
      </div>

      {unavailableReason ? (
        <p className={styles.Unavailable}>{unavailableMessage}</p>
      ) : (
        <div className={styles.Fields}>
          <Select
            className={styles.Select}
            options={options}
            value={selectedId}
            loading={loading}
            disabled={loading}
            placeholder={t('pages.ticketSummary.additionalServices.selectPlaceholder')}
            onChange={onSelect}
          />
          <Button
            kind="secondary"
            className={styles.SubmitButton}
            disabled={!isApplied && !selectedId}
            onClick={onToggle}
          >
            {isApplied
              ? t('pages.ticketSummary.additionalServices.remove')
              : t('pages.ticketSummary.additionalServices.add')}
          </Button>
        </div>
      )}

      <Divider className={styles.Divider} />

      <div className={styles.Service}>
        <FeatureIcon
          icon="canopy"
          description={t('pages.ticketSummary.additionalServices.canopyQuestion')}
        />
        <div className={styles.ServiceText}>
          <div className={styles.ServiceHeader}>
            <p className={styles.Question}>
              {t('pages.ticketSummary.additionalServices.canopyQuestion')}
            </p>
            {canopy.price > 0 && (
              <span className={styles.Price}>{priceFormat.format(canopy.price)}</span>
            )}
          </div>
          <p className={styles.Description}>
            {t('pages.ticketSummary.additionalServices.canopyDescription')}
          </p>
          <button
            type="button"
            className={styles.RequirementsToggle}
            onClick={() => setShowRequirements(!showRequirements)}
          >
            {showRequirements
              ? t('pages.ticketSummary.additionalServices.hideRequirements')
              : t('pages.ticketSummary.additionalServices.showRequirements')}
          </button>
          {showRequirements && (
            <ul className={styles.Requirements}>
              {canopyRequirements.map((requirement) => (
                <li className={styles.Requirement} key={requirement}>
                  {requirement}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className={styles.CanopyAction}>{renderCanopyAction()}</div>
    </div>
  );
};

export default AdditionalServicesCard;
